import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useFilters } from "../../context/FilterContext";
import {
    Box, TextField, MenuItem, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, TablePagination,
    Typography, CircularProgress, Tooltip, IconButton, FormControl,
    InputLabel, Select, InputAdornment
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { toast } from "react-toastify";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import { getReportForAdmin, getReportForReportViewer } from "../../services/reportServices/adminReportServices/AttendanceReportServices";
import { getAllSemesters } from "../../services/adminServices/CoeServices";
import AdminAttendanceModal from "../dailyReport/adminReport/AdminAttendanceModal";
import AdminAttendanceDetailsModal from "../dailyReport/adminReport/AdminAttendanceDetailsModal";
import AdminTotalRegisteredModal from "../dailyReport/adminReport/AdminTotalRegisteredModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#7e7e7e",
        color: theme.palette.common.white,
        textTransform: "capitalize",
        fontSize: 14,
        fontWeight: 600,
        whiteSpace: "nowrap"
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 13,
        textTransform: "capitalize",
        whiteSpace: "nowrap"
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd),&:nth-of-type(even)": {
        backgroundColor: theme.palette.action.hover,
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    "&:last-child td, &:last-child th": {
        border: 0,
    },
}));

const formatCount = (val) => {
    if (val === undefined || val === null || val === '') return '00';
    const num = Number(val);
    if (isNaN(num)) return val;
    return num >= 0 && num < 10 ? `0${num}` : String(num);
};

const extractErrorMessage = (err, fallback = "An error occurred") => {
    if (!err) return fallback;
    const resData = err?.response?.data;
    if (resData) {
        const details = resData?.error?.details || resData?.details;
        if (Array.isArray(details) && details.length > 0) {
            const detailMessages = details
                .map(d => (typeof d === 'string' ? d : d?.message || d?.msg || (d?.field ? `${d.field}: invalid` : '')))
                .filter(Boolean);
            if (detailMessages.length > 0) {
                return detailMessages.join(', ');
            }
        }
        if (typeof resData?.message === 'string' && resData.message.trim()) {
            return resData.message;
        }
        if (typeof resData?.error?.message === 'string' && resData.error.message.trim()) {
            return resData.error.message;
        }
        if (typeof resData?.error === 'string' && resData.error.trim()) {
            return resData.error;
        }
        if (typeof resData === 'string' && resData.trim() && !resData.trim().startsWith('<')) {
            return resData;
        }
    }
    if (typeof err?.message === 'string' && err.message.trim()) {
        return err.message;
    }
    return fallback;
};

const ReportViewerAttendance = () => {
    const { reportViewerAttendanceState, setReportViewerAttendanceState } = useFilters();

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    const userCoeId = user?.coeId || user?.coe_id || user?.coeCenter?.id || user?.coe_center_id || user?.coe || "";

    const [data, setData] = useState(reportViewerAttendanceState.data || []);
    const [apiStatistics, setApiStatistics] = useState(reportViewerAttendanceState.statistics || null);
    const [semesters, setSemesters] = useState(reportViewerAttendanceState.options?.semesters || []);
    const [semester, setSemester] = useState(() => reportViewerAttendanceState.selection?.semester || "");

    const [page, setPage] = useState(reportViewerAttendanceState.page || 0);
    const [rowsPerPage, setRowsPerPage] = useState(reportViewerAttendanceState.rowsPerPage || 50);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState({});
    const [selectedAttendance, setSelectedAttendance] = useState();
    const [open, setOpen] = useState(false);
    const [learnerModal, setLearnerModal] = useState(false);
    const [totalRegisteredModal, setTotalRegisteredModal] = useState(false);

    const [searchText, setSearchText] = useState(reportViewerAttendanceState.searchText || "");
    const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState(
        reportViewerAttendanceState.lastRefreshed ? new Date(reportViewerAttendanceState.lastRefreshed) : null
    );

    // Sync state with FilterContext
    useEffect(() => {
        setReportViewerAttendanceState(prev => ({
            ...prev,
            selection: { coe: userCoeId, semester },
            searchText,
            data,
            statistics: apiStatistics,
            showTable: true,
            page,
            rowsPerPage,
            lastRefreshed,
            options: { semesters },
            hasFetched: prev.hasFetched
        }));
    }, [
        userCoeId, semester, searchText, data, apiStatistics, page, rowsPerPage,
        lastRefreshed, semesters, setReportViewerAttendanceState
    ]);

    // Fetch Semesters
    useEffect(() => {
        if (!userCoeId) return;
        const fetchSemestersList = async () => {
            try {
                const res = await getAllSemesters(userCoeId);
                const sortedSemesters = res?.data?.data?.sort((a, b) => a - b) || [];
                setSemesters(sortedSemesters);
                setReportViewerAttendanceState(prev => ({
                    ...prev,
                    options: { ...prev.options, semesters: sortedSemesters }
                }));
            } catch (error) {
                setSemesters([]);
                console.error("Failed to fetch semesters:", error);
                toast.error(extractErrorMessage(error, "Failed to fetch semesters"));
            }
        };
        if (semesters.length === 0) fetchSemestersList();
    }, [userCoeId, semesters.length, setReportViewerAttendanceState]);

    // Fetch attendance data
    const fetchAttendanceData = useCallback(async (sem = semester, isSilent = false) => {
        if (!userCoeId) return;
        if (isSilent) {
            setIsAutoRefreshing(true);
        } else {
            setLoading(true);
        }
        try {
            const res = await getReportForReportViewer(userCoeId, sem || "");
            const reportData = res?.data?.data || [];
            const safeData = Array.isArray(reportData) ? reportData : [];
            const statsData = res?.data?.statistics || null;
            const now = new Date();
            setData(safeData);
            setApiStatistics(statsData);
            setLastRefreshed(now);
            setReportViewerAttendanceState(prev => ({
                ...prev,
                data: safeData,
                statistics: statsData,
                lastRefreshed: now,
                hasFetched: true
            }));
        } catch (error) {
            setData([]);
            setApiStatistics(null);
            if (!isSilent) {
                toast.error(extractErrorMessage(error, "Error fetching Attendance Report"));
            }
        } finally {
            setLoading(false);
            setIsAutoRefreshing(false);
        }
    }, [userCoeId, semester, setReportViewerAttendanceState]);

    // Initial load: automatically fetch data for COE ONLY IF not already fetched in context
    useEffect(() => {
        if (userCoeId && !reportViewerAttendanceState.hasFetched) {
            fetchAttendanceData(semester, false);
        }
    }, [userCoeId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 1-minute auto-refresh
    useEffect(() => {
        if (!userCoeId) return;
        const intervalId = setInterval(() => {
            fetchAttendanceData(semester, true);
        }, 60000);
        return () => clearInterval(intervalId);
    }, [userCoeId, semester, fetchAttendanceData]);

    const handleSemesterChange = (e) => {
        const val = e.target.value;
        setSemester(val);
        setPage(0);
        fetchAttendanceData(val, false);
    };

    const highlightText = (text, query) => {
        if (!query || !text) return text;
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, "gi");
        const parts = String(text).split(regex);
        return parts.map((part, index) =>
            regex.test(part) ? (
                <Box
                    component="mark"
                    key={index}
                    sx={{
                        backgroundColor: "#ffe082",
                        color: "#212529",
                        fontWeight: "inherit",
                        borderRadius: "2px",
                        px: "2px",
                        py: 0
                    }}
                >
                    {part}
                </Box>
            ) : (
                part
            )
        );
    };

    // Calculate Summary Stats for Top Cards
    const stats = useMemo(() => {
        if (apiStatistics) {
            return {
                totalLearners: Number(apiStatistics.totalLearners) || 0,
                practiceSessions: Number(apiStatistics.practiceSessions) || 0,
                maxAttendance: Number(apiStatistics.maxAttendance) || 0,
                averageAttendance: apiStatistics.averageAttendance !== undefined && apiStatistics.averageAttendance !== null
                    ? Number(apiStatistics.averageAttendance)
                    : 0,
                notAttended: Number(apiStatistics.notAttended) || 0
            };
        }

        if (!data || data.length === 0) {
            return {
                totalLearners: 0,
                practiceSessions: 0,
                maxAttendance: 0,
                averageAttendance: 0,
                notAttended: 0
            };
        }

        let totalLearners = 0;
        let maxAttendance = 0;
        let totalAttended = 0;
        let notAttended = 0;

        for (let i = 0; i < data.length; i++) {
            const item = data[i];
            totalLearners += (Number(item.TotalLearners) || 0);
            const attendedVal = Number(item.MaxAttended || item.Attended) || 0;
            if (attendedVal > maxAttendance) maxAttendance = attendedVal;
            totalAttended += (Number(item.Attended) || 0);
            notAttended += (Number(item.NotAttended) || 0);
        }

        const practiceSessions = data.length;
        const averageAttendance = practiceSessions > 0 ? Number((totalAttended / practiceSessions).toFixed(1)) : 0;

        return {
            totalLearners,
            practiceSessions,
            maxAttendance,
            averageAttendance,
            notAttended
        };
    }, [data, apiStatistics]);

    // Filter table data by search text
    const filteredData = useMemo(() => {
        if (!searchText) return data;
        const search = searchText.toLowerCase();
        return data.filter(row =>
            Object.values(row).some(value =>
                String(value || "").toLowerCase().includes(search)
            )
        );
    }, [data, searchText]);

    const sortedData = useMemo(() => {
        if (!filteredData || filteredData.length === 0) return [];
        return [...filteredData].sort((a, b) => {
            const timeA = a.AttendanceDate ? new Date(a.AttendanceDate).getTime() : 0;
            const timeB = b.AttendanceDate ? new Date(b.AttendanceDate).getTime() : 0;
            return timeB - timeA;
        });
    }, [filteredData]);

    const paginatedRows = useMemo(() =>
        sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedData, page, rowsPerPage]
    );

    // Modal click handlers
    const handleClick = (row) => {
        setSelectedAttendance(row);
        setOpen(true);
        setSelected({
            ...row,
            coe: userCoeId || row?.coeId || row?.coe_id || row?.coeID,
            coeId: userCoeId || row?.coeId || row?.coe_id || row?.coeID,
            labType: row?.lab_type || row?.labType,
            lab_type: row?.lab_type || row?.labType,
            SessionTime: row?.SessionTime || row?.timeSlot || row?.sessionTime,
            timeSlot: row?.SessionTime || row?.timeSlot || row?.sessionTime,
            attendanceName: row?.attendanceName || row?.attendance_name,
            AttendanceDate: row?.AttendanceDate || row?.attendanceDate || row?.forDate || row?.date,
            labId: row?.labId || row?.lab_id,
            semester: row?.semester || semester
        });
    };

    const handleOpenCreateModuleModal = (row, action) => {
        setLearnerModal(true);
        setSelected({
            ...row,
            coe: userCoeId,
            coe_name: row?.coe_name || row?.coeName,
            lab: row?.labId,
            lab_name: row?.lab_name || row?.labName,
            date: row?.AttendanceDate,
            attendanceId: row?.dailyAttendanceId,
            action,
            attendanceName: row?.attendanceName,
            semester: row?.semester || semester
        });
    };

    const handleOpenTotalRegisteredModal = (row) => {
        setTotalRegisteredModal(true);
        setSelected({
            coe: userCoeId,
            labId: row?.labId,
            semester: row?.semester || semester
        });
    };

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden', boxSizing: 'border-box' }}>
            {/* Filter Bar: Enclosed in Card Container like Learner Reviews */}
            <Box
                className="learner-review-filters-container"
                sx={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    p: '10px 16px',
                    mb: 2,
                    mt: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* Optional Semester Filter */}
                    <FormControl
                        size="small"
                        sx={{
                            width: { xs: '100%', sm: 190, md: 210 },
                            minWidth: { xs: '100%', sm: 170 },
                            flex: 'none'
                        }}
                    >
                        <InputLabel id="attendance-semester-label" shrink>Semester</InputLabel>
                        <Select
                            labelId="attendance-semester-label"
                            id="attendance-semester-select"
                            value={semester || ''}
                            onChange={handleSemesterChange}
                            label="Semester"
                            displayEmpty
                            notched
                            renderValue={(selected) => (selected !== '' && selected !== null && selected !== undefined) ? selected : "All Semesters"}
                            sx={{
                                backgroundColor: '#fff',
                                borderRadius: '8px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#1976d2',
                                },
                            }}
                        >
                            <MenuItem value="">All Semesters</MenuItem>
                            {semesters.filter(sem => sem != null).map((sem) => (
                                <MenuItem key={sem} value={sem}>
                                    {sem}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Auto-Refresh Status Indicator & Refresh Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.2,
                        ml: { md: 'auto', xs: 0 },
                        py: 0.5,
                        px: 0.5,
                        flexWrap: 'nowrap'
                    }}
                >
                    <Box
                        sx={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            backgroundColor: '#4caf50',
                            display: 'inline-block',
                            boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.25)',
                            flexShrink: 0
                        }}
                    />
                    <Typography
                        component="span"
                        sx={{
                            fontSize: '13px',
                            color: '#555',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        Auto-refreshes every 1 min{' '}
                        <Box component="span" sx={{ color: '#777', fontWeight: 400 }}>
                            (Last updated: {lastRefreshed ? lastRefreshed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true }) : ''})
                        </Box>
                    </Typography>

                    <Tooltip title="Refresh now" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => fetchAttendanceData(semester, false)}
                                disabled={loading || isAutoRefreshing}
                                sx={{
                                    p: 0.5,
                                    color: '#1976d2',
                                    '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <RefreshIcon
                                    sx={{
                                        fontSize: 20,
                                        color: '#1976d2',
                                        animation: (isAutoRefreshing || loading) ? 'spin 1s linear infinite' : 'none',
                                        '@keyframes spin': {
                                            '0%': { transform: 'rotate(0deg)' },
                                            '100%': { transform: 'rotate(360deg)' },
                                        },
                                    }}
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {/* TOP SUMMARY STAT CARDS GRID */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: 'repeat(1, 1fr)',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(4, 1fr)',
                        lg: 'repeat(4, 1fr)'
                    },
                    gap: '14px',
                    mb: 3,
                    width: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* 1. Total Learners - Blue Top Accent */}
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderTop: '3px solid #3b82f6',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
                        py: 1.6,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.12)',
                        }
                    }}
                >
                    <Typography sx={{ color: '#64748b', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', mb: '4px' }}>
                        Total Learners
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatCount(stats.totalLearners)}
                    </Typography>
                </Box>

                {/* 2. Practice Sessions - Indigo Top Accent */}
                {/* <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderTop: '3px solid #6366f1',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
                        py: 1.6,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.12)',
                        }
                    }}
                >
                    <Typography sx={{ color: '#64748b', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', mb: '4px' }}>
                        Practices Conducted
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatCount(stats.practiceSessions)}
                    </Typography>
                </Box> */}

                {/* 3. Max Attendance - Cyan/Teal Top Accent */}
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderTop: '3px solid #06b6d4',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
                        py: 1.6,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(6, 182, 212, 0.12)',
                        }
                    }}
                >
                    <Typography sx={{ color: '#64748b', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', mb: '4px' }}>
                        Max Attendance
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatCount(stats.maxAttendance)}
                    </Typography>
                </Box>

                {/* 4. Average Attendance - Purple Top Accent */}
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderTop: '3px solid #8b5cf6',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
                        py: 1.6,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.12)',
                        }
                    }}
                >
                    <Typography sx={{ color: '#64748b', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', mb: '4px' }}>
                        Average Attendance
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {stats.averageAttendance}
                    </Typography>
                </Box>

                {/* 5. Not Attended - Rose/Red Top Accent */}
                <Box
                    sx={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderTop: '3px solid #f43f5e',
                        borderRadius: '8px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02)',
                        py: 1.6,
                        px: 2,
                        minHeight: 'auto',
                        cursor: 'default',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.12)',
                        }
                    }}
                >
                    <Typography sx={{ color: '#64748b', fontSize: '11px', fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', mb: '4px' }}>
                        Not Attended
                    </Typography>
                    <Typography sx={{ color: '#0f172a', fontSize: '24px', fontWeight: 700 }}>
                        {formatCount(stats.notAttended)}
                    </Typography>
                </Box>
            </Box>

            {/* Loading Spinner during background fetch */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {/* EXISTING TABLE LAYOUT SHOWN BELOW THE CARDS */}
            <Box sx={{ width: '100%', mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#212529' }}>
                        Attendance Records ({filteredData.length})
                    </Typography>

                    {/* Search Attendance Input */}
                    <TextField
                        size="small"
                        placeholder="Search"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            setPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ color: '#757575', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchText ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchText('')}
                                        edge="end"
                                        aria-label="clear search"
                                    >
                                        <ClearIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{ minWidth: { xs: '100%', sm: 300 }, backgroundColor: '#fff', borderRadius: '8px' }}
                    />
                </Box>

                <TableContainer component={Paper} sx={{ width: "100%", maxWidth: "100%", minWidth: 0, overflowX: "auto", borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: 'none' }}>
                    <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
                        <TableHead>
                            <TableRow>
                                {[
                                    "Date", "LabName", "Session Time", "Semester",
                                    "Total Registered", "Max Attended",
                                    "#Attended", "#Not Attended", "% Attended"
                                ].map((header) => (
                                    <StyledTableCell key={header}>{header}</StyledTableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.length ? paginatedRows.map((row, idx) => (
                                <StyledTableRow key={idx}>
                                    <StyledTableCell>{highlightText(row.AttendanceDate || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell>{highlightText(row.lab_name || "-", searchText)}</StyledTableCell>
                                    {/* <StyledTableCell sx={{ textTransform: "none !important" }}>{highlightText(row.attendanceName || "-", searchText)}</StyledTableCell> */}
                                    <StyledTableCell>{highlightText(row.SessionTime || "-", searchText)}</StyledTableCell>
                                    <StyledTableCell>{highlightText(row.semester || "-", searchText)}</StyledTableCell>

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenTotalRegisteredModal(row)}
                                    >
                                        {highlightText(row.TotalLearners || "-", searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell
                                        onClick={() => handleClick(row)}
                                        style={{ color: "blue", cursor: "pointer", textDecoration: "underline" }}
                                    >
                                        {highlightText(row?.MaxAttended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenCreateModuleModal(row, "Present")}
                                    >
                                        {highlightText(row?.Attended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell
                                        sx={{ color: "blue !important", textDecoration: "underline", cursor: "pointer" }}
                                        onClick={() => handleOpenCreateModuleModal(row, "Absent")}
                                    >
                                        {highlightText(row?.NotAttended || 0, searchText)}
                                    </StyledTableCell>

                                    <StyledTableCell sx={{ fontWeight: 600 }}>
                                        {highlightText(Math.round(row.PercentageAttended) || 0, searchText)}%
                                    </StyledTableCell>
                                </StyledTableRow>
                            )) : (
                                <TableRow>
                                    <StyledTableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                        <Typography sx={{ color: '#6c757d', fontSize: '14px' }}>
                                            {searchText ? `No attendance records found matching "${searchText}"` : 'No attendance records found.'}
                                        </Typography>
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[10, 20, 30, 50, 100]}
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            </Box>

            {/* Attendance Details Modals */}
            <AdminTotalRegisteredModal
                selected={selected}
                open={totalRegisteredModal}
                onClose={() => setTotalRegisteredModal(false)}
            />

            <AdminAttendanceModal
                open={open}
                onClose={() => setOpen(false)}
                selected={selected}
                selectedAttendance={selectedAttendance}
            />

            <AdminAttendanceDetailsModal
                open={learnerModal}
                onClose={() => setLearnerModal(false)}
                selected={selected}
            />
        </Box>
    );
};

export default ReportViewerAttendance;
